import * as React from 'react';
import {
  mediaSrc,
  propertyAerialCoverUrl,
  propertyPanoramaCoverUrl,
  type CoverSize,
} from '../lib/properties.js';
import {
  propertyImageCascade,
  type PropertyImageInput,
  type PropertyImageSource,
} from '../lib/property-image.js';
import { useT } from '../lib/i18n.js';

/**
 * Krok UXV4a bod 4 → krok UXF1 bod 2 — VYKRESLENI OBRAZKU NEMOVITOSTI
 * (jedna komponenta).
 *
 * Poradi zdroju rozhoduje `lib/property-image.ts` (jedna funkce, testovatelna
 * bez prohlizece); tady je jen DOM a SESTUP KASKADOU: kdyz obrazek dane
 * urovne nedojde (panorama u adresy neexistuje, Mapy.com nedostupne, server
 * bez klice), `onError` posune index o patro niz. Nikdy se nekresli rozbity
 * `<img>`, prazdny obdelnik predstirajici mapu ani stred obce (predloha
 * § Pravidla 3).
 *
 * Krok DEMO8 bod 3: 404 na krytí (chybejici fotka + bez MAPY_API_KEY na verejnem
 * demu) se driv opakoval pri KAZDEM remountu karty (napr. periodicke obnoveni
 * Dneska) — stejna URL tak chodila na server znovu a znovu (log, 429).
 * `FAILED_COVER_URLS` je MODULOVY cache (prezije remount, ne jen jeden render):
 * jednou neuspesna URL se v teto relaci stranky uz nezkousi znovu, kaskada ji
 * preskoci rovnou na dalsi uroven.
 */
const FAILED_COVER_URLS = new Set<string>();

/** Jen pro testy — jinak by si jeden test donesl cache do druheho. */
export function resetFailedCoverUrlsForTests(): void {
  FAILED_COVER_URLS.clear();
}

function coverUrlOf(source: PropertyImageSource, size: CoverSize): string | null {
  switch (source.kind) {
    case 'photo':
      return mediaSrc(source.url);
    case 'panorama':
      return propertyPanoramaCoverUrl(source.propertyId, size);
    case 'aerial':
      return propertyAerialCoverUrl(source.propertyId, size);
    case 'none':
      return null;
  }
}

export interface PropertyImageProps extends PropertyImageInput {
  /** Rozmer obrazku v CSS pixelech — jde do dotazu na proxy (retina resi server). */
  readonly size?: CoverSize;
  /**
   * Trida VYKRESLENEHO prvku (ne obalu) — `rh-a2-kcard__cover` na karte
   * kanbanu, `rh-a2-hero` v detailu, nic uvnitr v4 foto karty (tam rozmer
   * drzi ramecek). Obal navic by zmenil rozvrzeni vsech volajicich.
   */
  readonly className?: string | undefined;
  /**
   * `data-testid` prefix. Urovne nesou `-cover`, `-cover-panorama`,
   * `-cover-aerial`, `-cover-none` — sada tim pozna, ktery zdroj to je.
   */
  readonly testIdPrefix: string;
}

export function PropertyImage({
  size = { width: 280, height: 172 },
  testIdPrefix,
  className = 'rh-v4-photo__src',
  ...input
}: PropertyImageProps): React.JSX.Element {
  const t = useT();
  const cascade = propertyImageCascade(input);
  const key = cascade.map((source) => JSON.stringify(source)).join('|');

  /*
   * Index urovne se resetuje V RENDERU, ne v `useEffect`: po zmene vstupu
   * (jina zakazka ve stejne karte) by efekt dobehl az po prvnim vykresleni a
   * karta by na snimek stihla ukazat zdroj predchozi nemovitosti.
   */
  const [level, setLevel] = React.useState(0);
  const [levelKey, setLevelKey] = React.useState(key);
  if (levelKey !== key) {
    setLevelKey(key);
    setLevel(0);
  }

  // Preskoc urovne, jejichz URL uz v tehle relaci jednou skoncila chybou —
  // zadny dalsi dotaz na tentyz obrazek (krok DEMO8 bod 3).
  let index = Math.min(level, cascade.length - 1);
  while (index < cascade.length - 1) {
    const url = coverUrlOf(cascade[index] ?? { kind: 'none' }, size);
    if (url === null || !FAILED_COVER_URLS.has(url)) break;
    index += 1;
  }
  const source = cascade[index] ?? { kind: 'none' as const };
  const fallDown = (): void => {
    const url = coverUrlOf(source, size);
    if (url !== null) FAILED_COVER_URLS.add(url);
    setLevel((current) => current + 1);
  };

  switch (source.kind) {
    case 'photo':
      return (
        <img
          src={mediaSrc(source.url)}
          crossOrigin="use-credentials"
          alt={t('webAdmin.gallery.photoAlt')}
          className={className}
          loading="lazy"
          data-testid={`${testIdPrefix}-cover`}
          onError={fallDown}
        />
      );
    case 'panorama':
      return (
        <img
          src={propertyPanoramaCoverUrl(source.propertyId, size)}
          alt={t('webAdmin.caseThumb.panoramaAlt')}
          className={className}
          loading="lazy"
          decoding="async"
          data-testid={`${testIdPrefix}-cover-panorama`}
          onError={fallDown}
        />
      );
    case 'aerial':
      return (
        <img
          src={propertyAerialCoverUrl(source.propertyId, size)}
          alt={t('webAdmin.caseThumb.orthoAlt')}
          className={className}
          loading="lazy"
          decoding="async"
          data-testid={`${testIdPrefix}-cover-aerial`}
          onError={fallDown}
        />
      );
    case 'none':
      /*
       * Krok GRAF5 (R.B.: „bez fotky decentní ortofoto parcely nebo vzor
       * parcely, ne prázdná plocha"). Veta „Fotka po nahrání" zustava — rika
       * pravdu o tom, co chybi — ale uz nestoji na HOLE sede plose: pod ni
       * je tyz jemny parcelni obrys, jaky kresli karta dokladu bez zakazky
       * (`DocumentCoverPlaceholder`, krok PIL5). Je to JEDEN vzor pro obe
       * mista; druha kopie cest by se s `brand2-identita.spec.ts` rozesla.
       */
      return (
        <span className={`${className} rh-v4-photo__none`} data-testid={`${testIdPrefix}-cover-none`}>
          <svg
            className="rh-v4-photo__doc-parcel"
            viewBox="0 0 600 280"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
          >
            <g fill="none" stroke="currentColor" strokeWidth={1.2} opacity={0.25}>
              {PARCEL_MOTIF_GROUPS.map((transform) => (
                <g key={transform} transform={transform}>
                  {PARCEL_MOTIF_PATHS.map((d) => (
                    <path key={d} d={d} />
                  ))}
                </g>
              ))}
            </g>
          </svg>
          <span className="rh-v4-photo__none-label">{t('webAdmin.caseThumb.photoPending')}</span>
        </span>
      );
  }
}

/**
 * Cesty 1:1 z `docs/design/brand-naves/vzory/parcelni-vzor.svg` (ctyri tvary,
 * ve vzoru trikrat zvetsene a rozmistene) — `brand2-identita.spec.ts` hlida,
 * ze se nerozejdou se zdrojem (tytez duvod jako `brand-icons.tsx`: barva jde
 * pres `currentColor`, coz holy `<img src>` neumi).
 */
const PARCEL_MOTIF_PATHS = [
  'M3 19L36 1Q39 -1 42 0L49 2Q54 3 51 10L35 49Q33 54 28 53L6 49Q2 49 2 44L0 26Q-1 22 3 19Z',
  'M62 8L94 14Q98 15 98 20L99 44Q99 49 94 47L53 34Q49 33 51 28L58 12Q59 7 62 8Z',
  'M8 59Q9 55 14 57L45 68Q50 70 48 75L40 96Q39 100 34 100H15Q11 100 10 96L2 69Q1 65 4 62Z',
  'M59 54Q60 49 65 51L95 56Q99 57 99 62L101 80Q102 85 97 87L61 100Q57 102 53 99Q50 97 52 92Z',
] as const;
const PARCEL_MOTIF_GROUPS = [
  'translate(20 15) scale(1.0784313725490196)',
  'translate(180 90) scale(1.5686274509803921)',
  'translate(360 10) scale(1.3725490196078431)',
] as const;

/**
 * Krok PIL5 — KARTA BEZ ZAKAZKY. Smlouva nebo faktura, ktera nenese zadny
 * `caseId`, nema ke ktere nemovitosti nahled dohledat — manual vizualni
 * identity (str. 11-12) proto misto ilustracni fotky nebo sede plochy
 * „Fotka po nahrání" (ta slibuje fotku, ktera tu ale NIKDY nemuze pribyt)
 * kresli jemny parcelni obrys s ikonou a typem dokladu.
 */
export function DocumentCoverPlaceholder({
  icon,
  label,
  testIdPrefix,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly testIdPrefix: string;
}): React.JSX.Element {
  return (
    <div className="rh-v4-photo__doc" data-testid={`${testIdPrefix}-cover-parcel`}>
      <svg
        className="rh-v4-photo__doc-parcel"
        viewBox="0 0 600 280"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
      >
        <g fill="none" stroke="currentColor" strokeWidth={1.2} opacity={0.25}>
          {PARCEL_MOTIF_GROUPS.map((transform) => (
            <g key={transform} transform={transform}>
              {PARCEL_MOTIF_PATHS.map((d) => (
                <path key={d} d={d} />
              ))}
            </g>
          ))}
        </g>
      </svg>
      <span className="rh-v4-photo__doc-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="rh-v4-photo__doc-label">{label}</span>
    </div>
  );
}
